import { useEffect, useMemo, useState } from "react";
import { MaintenanceSection, MaintenanceSectionContent, MaintenanceSectionHeader } from "@/components/layout/maintenance";
import { Alert } from "@/components/common/Alert/Alert";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { UnavailableOverlay } from "@/components/common/UnavailableOverlay/UnavailableOverlay";
import { fetchAiSubscription, fetchAiTokenUsage, fetchAiUserAccess } from "./api";
import { AiAccountSubscriptionCard } from "./components/AiAccountSubscriptionCard";
import { AiSectionHeading } from "./components/AiSectionHeading";
import { AiTokenUsageChart } from "./components/AiTokenUsageChart";
import { AiUserSubscriptionsTable } from "./components/AiUserSubscriptionsTable";
import type { AiAccountSubscription, AiUserAccessRow, SortKey, TokenUsageDay, TokenUsagePoint, UserAccessFilter } from "./types";

const monthNameFormatter = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });

const formatYearMonth = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const toTokenUsagePoints = (days: TokenUsageDay[]): TokenUsagePoint[] => {
  return days.map((day) => {
    const date = new Date(day.date);
    return {
      label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      day: `${date.getDate()}`,
      tokens: day.totalTokens,
    };
  });
};

export const AiPage = () => {
  const [search, setSearch] = useState("");
  const [userAccessFilter, setUserAccessFilter] = useState<UserAccessFilter>("all");
  const [sortColumn, setSortColumn] = useState<SortKey>("displayName");
  const [sortAsc, setSortAsc] = useState(true);

  const [subscription, setSubscription] = useState<AiAccountSubscription | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [hasLoadedSubscriptionOnce, setHasLoadedSubscriptionOnce] = useState(false);

  const [tokenUsageDays, setTokenUsageDays] = useState<TokenUsageDay[]>([]);
  const [isTokenUsageLoading, setIsTokenUsageLoading] = useState(true);
  const [tokenUsageError, setTokenUsageError] = useState<string | null>(null);
  const [tokenUsageMonthOffset, setTokenUsageMonthOffset] = useState(0);

  const [userAccessRows, setUserAccessRows] = useState<AiUserAccessRow[]>([]);
  const [isUserAccessLoading, setIsUserAccessLoading] = useState(true);
  const [userAccessError, setUserAccessError] = useState<string | null>(null);

  const tokenUsage = useMemo(() => toTokenUsagePoints(tokenUsageDays), [tokenUsageDays]);

  const tokenUsageMonthDate = useMemo(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + tokenUsageMonthOffset);
    return date;
  }, [tokenUsageMonthOffset]);

  const tokenUsageYearMonth = formatYearMonth(tokenUsageMonthDate);
  const tokenUsageMonthLabel = tokenUsageMonthOffset === 0 ? "Current month" : monthNameFormatter.format(tokenUsageMonthDate);

  const handlePrevTokenUsageMonth = () => setTokenUsageMonthOffset((offset) => offset - 1);
  const handleNextTokenUsageMonth = () => setTokenUsageMonthOffset((offset) => Math.min(offset + 1, 0));
  const handleCurrentTokenUsageMonth = () => setTokenUsageMonthOffset(0);

  // Token usage and user access are available whenever the subscription is usable -
  // either fully "active" or currently in a "trial" period.
  const isSubscriptionAvailable = ["active", "trial"].includes(subscription?.subscriptionStatus?.toLowerCase() ?? "");

  const loadSubscription = async () => {
    try {
      setIsSubscriptionLoading(true);
      setSubscriptionError(null);
      const result = await fetchAiSubscription();
      setSubscription(result);
    } catch (err) {
      console.error("Error fetching AI subscription: ", err);
      setSubscriptionError("Unable to load the account subscription.");
    } finally {
      setIsSubscriptionLoading(false);
      setHasLoadedSubscriptionOnce(true);
    }
  };

  useEffect(() => {
    loadSubscription();
  }, []);

  // Only fetch token usage once the account subscription has been resolved and is available -
  // avoids calling out for data the account isn't entitled to.
  useEffect(() => {
    if (isSubscriptionLoading) {
      return;
    }

    if (!isSubscriptionAvailable) {
      setIsTokenUsageLoading(false);
      setTokenUsageError(null);
      setTokenUsageDays([]);
      return;
    }

    const controller = new AbortController();

    const loadTokenUsage = async () => {
      try {
        setIsTokenUsageLoading(true);
        setTokenUsageError(null);
        const result = await fetchAiTokenUsage(tokenUsageYearMonth);
        if (!controller.signal.aborted) {
          setTokenUsageDays(result);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching AI token usage: ", err);
          setTokenUsageError("Unable to load token usage.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsTokenUsageLoading(false);
        }
      }
    };

    loadTokenUsage();

    return () => controller.abort();
  }, [isSubscriptionLoading, isSubscriptionAvailable, tokenUsageYearMonth]);

  const loadUserAccess = async () => {
    try {
      setIsUserAccessLoading(true);
      setUserAccessError(null);
      const result = await fetchAiUserAccess();
      setUserAccessRows(result);
    } catch (err) {
      console.error("Error fetching AI user access: ", err);
      setUserAccessError("Unable to load user access.");
    } finally {
      setIsUserAccessLoading(false);
    }
  };

  // Only fetch user access once the account subscription has been resolved and is available -
  // avoids calling out for data the account isn't entitled to.
  useEffect(() => {
    if (isSubscriptionLoading) {
      return;
    }

    if (!isSubscriptionAvailable) {
      setIsUserAccessLoading(false);
      setUserAccessError(null);
      setUserAccessRows([]);
      return;
    }

    loadUserAccess();
  }, [isSubscriptionLoading, isSubscriptionAvailable]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    const filtered = userAccessRows.filter((user) => {
      const matchesSearch = !normalizedSearch
        || [user.displayName, user.username, user.emailAddress].some((field) => field?.toLowerCase().includes(normalizedSearch));

      const matchesUserAccessFilter = userAccessFilter === "all"
        || (userAccessFilter === "enabled" && user.enabled)
        || (userAccessFilter === "disabled" && !user.enabled);

      return matchesSearch && matchesUserAccessFilter;
    });

    const sorted = [...filtered].sort((left, right) => {
      const leftValue = left[sortColumn];
      const rightValue = right[sortColumn];

      const comparison = typeof leftValue === "boolean"
        ? Number(leftValue) - Number(rightValue as boolean)
        : String(leftValue).localeCompare(String(rightValue));

      return sortAsc ? comparison : -comparison;
    });

    return sorted;
  }, [userAccessRows, search, userAccessFilter, sortColumn, sortAsc]);

  const handleSort = (newSortColumn: SortKey, newSortAsc: boolean) => {
    setSortColumn(newSortColumn);
    setSortAsc(newSortAsc);
  };

  const isAiUnavailable = !isSubscriptionLoading && !subscriptionError && !isSubscriptionAvailable;
  const hasSubscription = isSubscriptionLoading || !!subscriptionError || subscription !== null;

  return (
    <MaintenanceSection>
      {hasSubscription && (
        <MaintenanceSectionHeader
          title="AI"
          subtitle="AI maintenance and configuration for administrative setup."
        />
      )}
      <MaintenanceSectionContent>
        {!hasLoadedSubscriptionOnce ? (
          <LoadingPage message="Loading AI..." />
        ) : (
          <div className="grid gap-2 min-w-0">
            <AiAccountSubscriptionCard
              subscription={subscription}
              isLoading={isSubscriptionLoading}
              error={subscriptionError}
              onAccessModeChanged={loadSubscription}
            />
            {hasSubscription && (
              <>
                <UnavailableOverlay active={isAiUnavailable}>
                  <AiTokenUsageChart
                    data={tokenUsage}
                    monthLabel={tokenUsageMonthLabel}
                    monthDate={tokenUsageMonthDate}
                    usageLimit={subscription?.usageLimit}
                    isCurrentMonth={tokenUsageMonthOffset === 0}
                    onPrevMonth={handlePrevTokenUsageMonth}
                    onNextMonth={handleNextTokenUsageMonth}
                    onCurrentMonth={handleCurrentTokenUsageMonth}
                    isNextMonthDisabled={tokenUsageMonthOffset >= 0}
                    isLoading={isTokenUsageLoading}
                    error={tokenUsageError}
                  />
                </UnavailableOverlay>
                <UnavailableOverlay active={isAiUnavailable}>
                  {subscription?.grantAllUsers ? (
                    <div className="min-w-0">
                      <AiSectionHeading title="User access" />
                      <Alert variant="information" persistent>
                        All users in this account currently have AI access. Switch to allowlist mode to manage individual access.
                      </Alert>
                    </div>
                  ) : (
                    <AiUserSubscriptionsTable
                      rows={filteredUsers}
                      existingUserIds={userAccessRows.map((user) => user.userId)}
                      search={search}
                      userAccessFilter={userAccessFilter}
                      sortColumn={sortColumn}
                      sortAsc={sortAsc}
                      isLoading={isUserAccessLoading}
                      error={userAccessError}
                      onSearchChange={setSearch}
                      onUserAccessFilterChange={setUserAccessFilter}
                      onSort={handleSort}
                      onUserAccessChanged={loadUserAccess}
                    />
                  )}
                </UnavailableOverlay>
              </>
            )}
          </div>
        )}
      </MaintenanceSectionContent>
    </MaintenanceSection>
  );
};
